import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BellRing, BellOff } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const STAFF = ["teacher", "super_admin", "shop_admin"];
let ctx = null;

function unlockAudio() {
    try {
        ctx = ctx || new (window.AudioContext || window.webkitAudioContext)();
        if (ctx.state === "suspended") ctx.resume();
    } catch { /* audio non supporté */ }
}

export function playChime() {
    unlockAudio();
    if (!ctx) return;
    [[880, 0], [1320, 0.18]].forEach(([f, t]) => {
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.frequency.value = f; o.type = "sine";
        g.gain.setValueAtTime(0.0001, ctx.currentTime + t);
        g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + t + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t + 0.5);
        o.connect(g).connect(ctx.destination); o.start(ctx.currentTime + t); o.stop(ctx.currentTime + t + 0.55);
    });
}

// Global watcher: sound + toast + browser notification when a new question reaches the teacher/admin.
export function StaffAlerts() {
    const { user } = useAuth();
    const nav = useNavigate();
    const prev = useRef(null);
    const active = user && STAFF.includes(user.role);

    useEffect(() => {
        if (!active) return undefined;
        document.addEventListener("pointerdown", unlockAudio, { once: true });
        const base = user.role === "teacher" ? "/prof" : "/admin/support";
        const check = () => api.get("/support/unread-count").then(({ data }) => {
            const n = data.unread || 0;
            if (prev.current !== null && n > prev.current) {
                const l = data.latest;
                const body = l ? `${l.student_name || "Un élève"} — ${l.subject}` : "Un élève attend votre réponse.";
                const open = () => nav(l ? `${base}/${l.id}` : base);
                playChime();
                window.dispatchEvent(new Event("su:staff-alert"));
                toast("Nouvelle question d'un élève", { description: body, action: { label: "Ouvrir", onClick: open }, duration: 10000 });
                if ("Notification" in window && Notification.permission === "granted") {
                    const notif = new Notification("Savoir Utile — nouvelle question", { body, icon: "/icon-192.png", tag: `ticket-${l?.id || "new"}` });
                    notif.onclick = () => { window.focus(); open(); notif.close(); };
                }
            }
            prev.current = n;
        }).catch(() => {});
        check();
        const t = setInterval(check, 15000);
        return () => { clearInterval(t); prev.current = null; };
    }, [active, user?.role, nav]); // eslint-disable-line react-hooks/exhaustive-deps

    return null;
}

export function AlertsToggle({ dark = true }) {
    const supported = typeof window !== "undefined" && "Notification" in window;
    const [perm, setPerm] = useState(supported ? Notification.permission : "unsupported");
    const enable = async () => {
        unlockAudio();
        if (supported && perm === "default") setPerm(await Notification.requestPermission());
        playChime();
    };
    const on = perm === "granted";
    const Icon = perm === "denied" ? BellOff : BellRing;
    const label = on ? "Alertes activées — tester le son" : perm === "denied" ? "Notifications bloquées par le navigateur — tester le son" : "Activer les alertes (son + notification)";
    return (
        <button type="button" onClick={enable} className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm transition-colors hover:opacity-90"
            style={{ background: on ? "rgba(117,138,101,0.25)" : "rgba(201,164,88,0.18)", color: dark ? "var(--text-on-dark)" : "var(--text-primary)" }}
            data-testid="staff-alerts-toggle" data-permission={perm}>
            <Icon className="w-4 h-4" /> {label}
        </button>
    );
}
